import styles from './styles.scss';
import { NO_ELEMENT, debounce, rm } from 'utils';
import { SuggestedAddress } from 'models/SuggestedAddress';
import { postAddressSuggestions, postZipcodeSuggestions } from 'services/ApiCheckoutPageService';
import { useEffect, Reducer, useReducer, MouseEvent } from 'react';

interface OwnProps {
  addressInput: string;
  onSelect: (suggestion: SuggestedAddress) => void;
  visible: boolean;
  type: suggestionType;
}

interface OwnState {
  suggestions: SuggestedAddress[];
  visible: boolean;
  loading: boolean;
}

type suggestionType = 'address' | 'zipCode';

const initialState: OwnState = {
  suggestions: [],
  visible: false,
  loading: false
};

type Action =
  | { type: 'suggestions'; value: SuggestedAddress[] }
  | { type: 'loading'; value: boolean };

const ownReducer: Reducer<OwnState, Action> = (state, action) => {
  switch (action.type) {
    case 'suggestions':
      return { ...state, suggestions: action.value };
    case 'loading':
      return { ...state, loading: action.value };
    default:
      return state;
  }
};

const SUGGESTION_TIMEOUT = 300;
const getAddressSuggestions = debounce(
  (value: string, dispatch: (value: Action) => void) =>
    void postAddressSuggestions(value).then((response: SuggestedAddress[]) => {
      dispatch({ type: 'suggestions', value: response });
      dispatch({ type: 'loading', value: false });
    }),
  SUGGESTION_TIMEOUT
);

const getZipCodeSuggestions = debounce(
  (value: string, dispatch: (value: Action) => void) =>
    void postZipcodeSuggestions(value).then((response: SuggestedAddress[]) => {
      dispatch({ type: 'suggestions', value: response });
      dispatch({ type: 'loading', value: false });
    }),
  SUGGESTION_TIMEOUT
);

const getKey = (suggestion: SuggestedAddress) => {
  return `${suggestion.street}.${suggestion.postalcode}.${suggestion.locality}`;
};

interface SuggestionProps {
  address: SuggestedAddress;
  onSelect: (suggestion: SuggestedAddress) => void;
  type: suggestionType;
}

const Suggestion = ({ address, onSelect, type }: SuggestionProps) => {
  const preventDefault = (e: MouseEvent<Element>) => {
    e.preventDefault();
  };

  return (
    <a className={styles.suggestion} onMouseDown={preventDefault} onClick={() => onSelect(address)}>
      {type === 'zipCode'
        ? `${address.postalcode} ${address.locality}`
        : `${address.street ?? ''} ${address.streetNumber ?? ''}${address.letter ?? ''}, ${
            address.postalcode
          } ${address.locality}`}
    </a>
  );
};

export const AddressSuggestion = ({ onSelect, visible, addressInput, type }: OwnProps) => {
  const [state, dispatch] = useReducer(ownReducer, initialState);

  useEffect(() => {
    if (!visible) {
      return;
    }

    dispatch({ type: 'loading', value: true });

    switch (type) {
      case 'address':
        getAddressSuggestions(addressInput, dispatch);
        break;
      case 'zipCode':
        getZipCodeSuggestions(addressInput, dispatch);
        break;

      default:
        break;
    }
  }, [addressInput, visible, type]);

  if (!visible) {
    return NO_ELEMENT;
  }

  return (
    <div className={styles.suggestionContainer}>
      {state.loading && <div className={styles.suggestion}>{rm('common_loading')}...</div>}
      {!state.loading && state.suggestions.length === 0 && (
        <div className={styles.suggestion}>{rm('customerpages_address_not_found')}</div>
      )}
      {!state.loading &&
        state.suggestions.length > 0 &&
        state.suggestions.map(s => (
          <Suggestion address={s} onSelect={onSelect} key={getKey(s)} type={type} />
        ))}
    </div>
  );
};
